import { Id, Identifiable, List, Milestone } from "@planzone/dto";
import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { resizeList } from "./utils/resizeList";
import { reduceMilestone } from "../milestone";
import { reduceTask } from "../task";

function editMilestones(
  list: Identifiable<List>,
  data: Boundaries["editMilestone"]["success"],
) {
  return list.milestones.reduce<{
    hasMatch: boolean;
    milestones: Identifiable<Milestone>[];
  }>(
    ({ hasMatch, milestones }, m) => ({
      hasMatch: hasMatch || m.uuid === data.milestone.uuid,
      milestones: [
        ...milestones,
        reduceMilestone(m, {
          type: events.editMilestoneSucceeded,
          data,
        }),
      ],
    }),
    { hasMatch: false, milestones: [] },
  );
}

function editSubLists(
  list: Identifiable<List>,
  milestone: Boundaries["editMilestone"]["success"],
  impactedLists: {
    uuid: Id;
    endDate: string;
    startDate: string;
    minStartDate?: string;
    maxStartDate?: string;
    minEndDate?: string;
    maxEndDate?: string;
  }[],
) {
  return list.subLists.reduce<{
    hasMatch: boolean;
    subLists: Identifiable<List>[];
  }>(
    ({ hasMatch, subLists }, subList) => {
      const { hasMatch: hasSubListMatch, list: nextSubList } = editList(
        subList,
        milestone,
        impactedLists,
      );

      return {
        hasMatch: hasMatch || hasSubListMatch,
        subLists: [...subLists, nextSubList],
      };
    },
    { hasMatch: false, subLists: [] },
  );
}

function editList(
  list: Identifiable<List>,
  data: Boundaries["editMilestone"]["success"],
  impactedLists: {
    uuid: Id;
    endDate: string;
    startDate: string;
    minStartDate?: string;
    maxStartDate?: string;
    minEndDate?: string;
    maxEndDate?: string;
  }[],
): { hasMatch: boolean; list: Identifiable<List> } {
  const { hasMatch, milestones } = editMilestones(list, data);
  const impactedList = impactedLists.find(({ uuid }) => uuid === list.uuid);
  const { hasMatch: hasSubListMatch, subLists } = editSubLists(
    list,
    data,
    impactedLists,
  );
  const nextList = {
    ...list,
    ...(impactedList
      ? {
          startDate: impactedList.startDate,
          endDate: impactedList.endDate,
          ...(impactedList.minStartDate !== undefined && {
            minStartDate: impactedList.minStartDate,
          }),
          ...(impactedList.maxStartDate !== undefined && {
            maxStartDate: impactedList.maxStartDate,
          }),
          ...(impactedList.minEndDate !== undefined && {
            minEndDate: impactedList.minEndDate,
          }),
          ...(impactedList.maxEndDate !== undefined && {
            maxEndDate: impactedList.maxEndDate,
          }),
        }
      : {}),
    milestones,
    tasks: list.tasks.map((task) =>
      reduceTask(task, {
        type: events.editMilestoneSucceeded,
        data,
      }),
    ),
    subLists,
  };
  const isMatching = hasMatch || hasSubListMatch;

  return {
    hasMatch: isMatching,
    list: isMatching ? resizeList(nextList, data.milestone.endDate) : nextList,
  };
}

function handleEditMilestone(
  list: Identifiable<List>,
  milestone: Boundaries["editMilestone"]["success"],
): Identifiable<List> {
  const impactedLists = milestone.impactedElements
    ? milestone.impactedElements.filter(({ type }) => type === "list")
    : [];

  return editList(list, milestone, impactedLists).list;
}

export { handleEditMilestone };
